import $  = require('jquery');
import ModulWithRedux from './ModulWithRedux';
import l10n from '../util/l10n';
import TextModuleData from './text/ModuleData';
import { TextEditViewTextBasics, TextEditViewFormat } from './text/EditView';
import WebView from './text/WebView';
import ModuleInlineEditor from './common/js/ModuleInlineEditor';
import diyLightTheme = require('../material-ui/diyLightTheme');
import { setUndoState, setRedoState } from '../module-action-bar/index';
import AppRegistry from "diy/AppRegistry";
import TabOptions from '../actionBar/interfaces/TabOptions';

function getStore() {
    return AppRegistry.getStoreProvider().getStore();
}

interface CommandListener {
    (this: {state: number}): void;
}

export default class ModulFliestext extends ModulWithRedux<TextModuleData> {

    hasUndoRedo = true;
    helpArticleId = 'textfield';

    protected data: TextModuleData;
    private changeEventTriggered = false;
    private ckEditorPromise: Promise<CKEditor>;
    private ckEditorPromiseResolve: (ckEditor: CKEditor) => void;
    private inlineEditor: ModuleInlineEditor = null;

    constructor(moduleId: number) {
        super(moduleId);
        this.ckEditorPromise = new Promise<CKEditor>((resolve, reject) => {
            this.ckEditorPromiseResolve = resolve;
        });
    }

    loadData(data: TextModuleData) {
        this.data = data;
        // 2 reasons why we don't receive data.text but need to
        // get it from the rendered module content
        // 1. We don't duplicate the data
        // 2. There are issues with the current way of sending this data from the server (render an inline <script> tag)
        // because data.text can contain arbitrary HTML, and there are many cases
        // when this would result in an invalid XML and so cannot be rendered at all
        this.data.text = this.getContentElement().innerHTML;
    }

    /**
     * @override
     * strictly for typing purposes
     */
    getData() {
        return this.data;
    }

    onOpenTracking() {
        if (!this.getIsNew()) {
            let self = this;
            $(document).on('diy-text-editor-clicked', (ev, dat) => {
                /**
                 * Make some SiteCatalyst tracking
                 */
                $(document).trigger('diy-editor-makeWebsiteFit', {
                    'condition': 'Tried',
                    'name': 'Content',
                    'otherData': self.moduleType
                });
            });
        }
    }

    getCKEditorPromise() {
        return this.ckEditorPromise;
    }

    getActionBarTabs(): TabOptions[] {
        return [
            {
                label: l10n('module.text.tabs.basic'),
                content: this.getConnectedComponent(TextEditViewTextBasics)
            },
            {
                label: l10n('module.text.tabs.advanced'),
                content: this.getConnectedComponent(TextEditViewFormat)
            }
        ];
    }

    undoHandler = () => {
        this.inlineEditor.getEditor().execCommand('undo');
    }
    redoHandler = () => {
        this.inlineEditor.getEditor().execCommand('redo');
    }

    /**
     * @override
     */
    getSaveData() {
        const data = super.getSaveData();
        data.text = this.inlineEditor.getHtmlData();
        return data;
    }

    save() {
        if (!this.getIsNew()) {
            /**
             * Make some SiteCatalyst tracking
             */
            $(document).trigger('diy-editor-makeWebsiteFit', {
                'condition': 'Applied',
                'name': 'Content',
                'otherData': this.moduleType
            });

            if (this.inlineEditor.isDirty()) {
                $(document.body).trigger('ContentModuleSaved.BSA');
            }
        }

        return super.save();
    }

    /**
     * @override
     */
    close(userClose?: boolean, cancelEdit?: boolean, isSaving?: boolean, isUserAction?: boolean) {
        this.inlineEditor.stopEditing();
        super.close(userClose, cancelEdit, isSaving, isUserAction);
    }

    initCKEditor(textElement: HTMLElement) {
        const {
            textSanitizeHtml, textPasteAsPlainText
        } = this.getData().supports;

        this.inlineEditor = new ModuleInlineEditor(this, textElement);

        this.inlineEditor.startEditing({
            'diy-moduleType': this.moduleType,
            'diy-excludePasteScrubbers': 'semanticplaceholders',
            toolbar: 'Text',
            toolbar_Text: this.getCkeditorToolbarConfig(),
            diyhelpLabel: 'textfield',
            format_tags: 'p;h1;h2;pre',
            extraPlugins: ',lineutils,widget,diysemanticplaceholder',
            allowedContent: !textSanitizeHtml,
            forcePasteAsPlainText: textPasteAsPlainText,
            pasteFromWordRemoveFontStyles: false,
            pasteFromWordRemoveStyles: false,
            // this setting is primary used for setting the z-index
            // of CKEditor dialogs, and these should be the same as
            // standard Material UI dialogs zIndex
            baseFloatZIndex: diyLightTheme.zIndex.dialog,
            hideToolbar: true
        });

        this.inlineEditor.editor.on('pluginsLoaded', (ev: any) => {
            ev.editor.focus();
        });

        this.inlineEditor.editor.on('instanceReady', () => {
            this.ckEditorPromiseResolve(this.inlineEditor.getEditor());

            const store = getStore();

            this.inlineEditor.editor.getCommand('undo').on('state', function() {
                store.dispatch(setUndoState({enabled: this.state !== CKEDITOR.TRISTATE_DISABLED}));
            } as CommandListener);

            this.inlineEditor.editor.getCommand('redo').on('state', function() {
                store.dispatch(setRedoState({enabled: this.state !== CKEDITOR.TRISTATE_DISABLED}));
            } as CommandListener);

        });

        // attach kpi and bsa events for changes inside the editor
        if (this.getIsNew() !== true) {
            $(document.body).trigger('ContentModuleOpened.BSA');
            this.inlineEditor.editor.on('change',  (ev: any) => {
                if (this.changeEventTriggered === false) {
                    jQuery(document.body).trigger('ContentModuleEdited.BSA');
                    jQuery(document).trigger('diy-text-editor-clicked');
                    this.changeEventTriggered = true;
                }
            });
        }
    }

    getInPlaceEditViewClass(): any {
        return null;
    }

    getWebViewClass() {
        return WebView;
    }

    private getCkeditorToolbarConfig() {
        const {
            textFormat, textAlign, textIndent, textStyle, textColor, textRemoveFormat, textHtmlEdit, textWordPaste
        } = this.getData().supports;

        let config = [
            [ 'Undo', 'Redo' ],
            [ 'FontSize' ],
            textFormat ? [ 'Format' ] : undefined,
            [ 'Bold', 'Italic', 'Underline', 'Strike', 'Superscript', 'Subscript' ],
            textAlign ? [ 'JustifyLeft', 'JustifyCenter', 'JustifyRight' ] : undefined,
            [ 'DiySemanticPlaceholder' ],
            [ 'ToggleCollapse' ],
            [ 'HelpButton' ],
            [ 'CloseButton' ],
            '/',
            [ 'NumberedList', 'BulletedList' ],
            textIndent ? [ 'Outdent', 'Indent' ] : undefined,
            textStyle || textColor ? [
                textStyle ? 'StyleButton' : undefined,
                textColor ? 'DiyTextColor' : undefined
            ] : undefined,
            [
                'PasteText',
                textRemoveFormat ? 'RemoveFormat' : undefined
            ],
            [
                textHtmlEdit ? 'Sourcedialog' : undefined,
                textWordPaste ? 'PasteFromWord' : undefined,
                'SelectAll'
            ],
            [ 'DiyLink', 'DiyUnlink' ],
            // XXX Including support for table, even if no controls in Action Bar
            // because users wants to keep copy-pasted tables, and CKEditor should
            // not strip them away
            '/',
            ['Table'], ['TableMixed'],
            ['TableInsertRowBefore', 'TableInsertRowAfter', 'TableDeleteRow'],
            ['TableInsertColumnBefore', 'TableInsertColumnAfter', 'TableDeleteColumn'],
            ['TableSplitVertically', 'TableSplitHorizontally'],
            ['TableMergeRight', 'TableMergeDown', 'TableMerge']
        ];

        return config.filter(item => item !== undefined);
    }

}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/module/ModulFliestext.ts
 **/